import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Engagement = {
  id: number
  code: string
  name: string
  account: number
  account_name: string | null
  opportunity: number | null
  opportunity_name: string | null
  partner: number | null
  partner_name: string | null
  authorising_contract: number | null
  authorising_contract_number: string | null
  engagement_manager: number | null
  engagement_manager_person: number | null
  engagement_manager_name: string | null
  partner_in_charge: number | null
  partner_in_charge_person: number | null
  partner_in_charge_name: string | null
  start_date: string | null
  end_date: string | null
  currency_code: string
  total_contract_value: string
  status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'DELIVERED' | 'CLOSED' | 'CANCELLED'
  notes: string
  project_count: number
}

type Account = { id: number; name: string }
type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

type Project = {
  id: number; code: string; name: string
  engagement: number; engagement_code: string; engagement_name: string
  account_name: string | null
  billing_type: 'T_AND_M' | 'FIXED_FEE' | 'MILESTONE' | 'RETAINER' | 'NON_BILLABLE'
  manager: number | null; manager_name: string | null
  status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'DELIVERED' | 'CLOSED' | 'CANCELLED'
  budget_amount: string; currency_code: string
  start_date: string | null; end_date: string | null
  phase_count: number; task_count: number
  deliverable_count: number; milestone_count: number
}

// ─────────────────── Constants ───────────────────
const STATUS_COLOR: Record<Engagement['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const BILLING_LABEL: Record<Project['billing_type'], string> = {
  T_AND_M: 'Time & Materials',
  FIXED_FEE: 'Fixed Fee',
  MILESTONE: 'Milestone',
  RETAINER: 'Retainer',
  NON_BILLABLE: 'Non-billable',
}

type TabKey = 'details' | 'projects' | 'files'

// ─────────────────── Page ───────────────────
export default function ConsultingEngagementDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const engagementId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const engagementQ = useQuery({
    queryKey: ['consulting', 'engagement', engagementId],
    queryFn: async () => (await api.get<Engagement>(`/consulting/engagements/${engagementId}/`)).data,
    enabled: !!engagementId,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<{ id: number; full_name: string }[]>('/hr/persons/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  type ContractStub = { id: number; contract_number: string; title: string; contract_type: string }
  const contractsQ = useQuery({
    queryKey: ['finance', 'vendor-contracts'],
    queryFn: async () => (await api.get<ContractStub[]>('/partners/vendor-contracts/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Engagement>) =>
      (await api.patch(`/consulting/engagements/${engagementId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'engagement', engagementId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'engagements'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/consulting/engagements/${engagementId}/`),
    onSuccess: () => {
      toast.success('Engagement deleted')
      qc.invalidateQueries({ queryKey: ['consulting', 'engagements'] })
      navigate('/admin/consulting/engagements')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (engagementQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (engagementQ.isError || !engagementQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Engagement not found.</p>
          <Link to="/admin/consulting/engagements" className="text-blue-600 hover:underline">← Back to engagements</Link>
        </div>
      </AdminLayout>
    )
  }

  const engagement = engagementQ.data
  const accounts = accountsQ.data || []
  const persons = personsQ.data || []
  const partners = (partnersQ.data || []).filter((p) => p.is_active)

  const save = (payload: Partial<Engagement>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  const canDelete = engagement.project_count === 0

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/consulting/engagements" className="text-muted-foreground hover:text-foreground hover:underline">← Engagements</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{engagement.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={engagement.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              {engagement.account_name || <span className="italic">No account</span>}
              {engagement.partner_name && <span> · {engagement.partner_name}</span>}
              {engagement.opportunity_name && (
                <span>
                  {' · '}
                  <Link to={`/admin/crm/opportunities`} className="hover:underline">
                    from {engagement.opportunity_name}
                  </Link>
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GenerateDocumentButton
              sourceType="consulting.Engagement"
              sourceId={engagement.id}
              label="Generate document…"
            />
            <InlineStatus
              value={engagement.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'ON_HOLD', label: 'On hold' },
                { value: 'DELIVERED', label: 'Delivered' },
                { value: 'CLOSED', label: 'Closed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              onSave={(v) => save({ status: v })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!canDelete}
                  onClick={() => canDelete && confirm(
                    `engagement ${engagement.code}`,
                    () => deleteMut.mutate(),
                  )}
                  className={canDelete ? 'text-destructive' : ''}
                >
                  {canDelete ? 'Delete engagement' : `Delete (${engagement.project_count} projects — block)`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'projects', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'projects' && `Projects (${engagement.project_count})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          engagement={engagement}
          accounts={accounts}
          persons={persons}
          partners={partners}
          contracts={contractsQ.data || []}
          onSave={save}
        />
      )}
      {tab === 'projects' && (
        <ProjectsTab
          engagementId={engagement.id}
          engagementCurrency={engagement.currency_code}
          persons={persons}
        />
      )}
      {tab === 'files' && <FilesTab parent="consulting.engagement" parentId={engagementId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

type ContractStub = { id: number; contract_number: string; title: string; contract_type: string }

function DetailsTab({
  engagement, accounts, persons, partners, contracts, onSave,
}: {
  engagement: Engagement
  accounts: Account[]
  persons: { id: number; full_name: string }[]
  partners: Partner[]
  contracts: ContractStub[]
  onSave: (payload: Partial<Engagement>) => Promise<void>
}) {
  return (
    <div className="bg-card rounded-lg shadow p-6 max-w-3xl">
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        <Row label="Engagement name">
          <InlineText value={engagement.name} onSave={(v) => onSave({ name: v })} />
        </Row>

        <Row label="Account (client)">
          <InlineSelect
            value={String(engagement.account)}
            displayLabel={engagement.account_name || <Dash />}
            options={accounts.map((a) => ({
              value: String(a.id), label: a.name, searchText: a.name,
            }))}
            onSave={(v) => onSave({ account: Number(v) })}
            helper="Changing the client re-parents this engagement under a different CRM account."
          />
        </Row>

        <Row label="Billing partner">
          <InlineSelect
            value={engagement.partner ? String(engagement.partner) : ''}
            displayLabel={engagement.partner_name || <Dash />}
            allowClear
            options={partners.map((p) => ({
              value: String(p.id), label: p.name, searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: v ? Number(v) : null })}
            helper="Optional. Third-party partner involved in delivery / billing."
          />
        </Row>

        <Row label="Authorising contract">
          <InlineSelect
            value={engagement.authorising_contract ? String(engagement.authorising_contract) : ''}
            displayLabel={
              engagement.authorising_contract_number ? (
                <Link
                  to={`/admin/partners/vendor-contracts/${engagement.authorising_contract}`}
                  className="text-blue-600 hover:underline font-mono text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {engagement.authorising_contract_number}
                </Link>
              ) : <span className="text-muted-foreground italic text-xs">none</span>
            }
            allowClear
            options={contracts.map((c) => ({
              value: String(c.id),
              label: c.contract_number,
              searchText: `${c.contract_number} ${c.title} ${c.contract_type}`,
            }))}
            onSave={(v) => onSave({ authorising_contract: v ? Number(v) : null } as Partial<Engagement>)}
            helper="The vendor SOW, MSA, or ICA under which this engagement is delivered."
          />
        </Row>

        <Row label="Engagement manager">
          <InlineSelect
            value={engagement.engagement_manager_person ? String(engagement.engagement_manager_person) : ''}
            displayLabel={engagement.engagement_manager_name || <Dash />}
            allowClear
            options={persons.map((p) => ({
              value: String(p.id), label: p.full_name, searchText: p.full_name,
            }))}
            onSave={(v) => onSave({ engagement_manager_person: v ? Number(v) : null })}
          />
        </Row>

        <Row label="Partner in charge">
          <InlineSelect
            value={engagement.partner_in_charge_person ? String(engagement.partner_in_charge_person) : ''}
            displayLabel={engagement.partner_in_charge_name || <Dash />}
            allowClear
            options={persons.map((p) => ({
              value: String(p.id), label: p.full_name, searchText: p.full_name,
            }))}
            onSave={(v) => onSave({ partner_in_charge_person: v ? Number(v) : null })}
            helper="Senior accountable partner / director from the firm side."
          />
        </Row>

        <Row label="Start date">
          <InlineDate value={engagement.start_date} onSave={(v) => onSave({ start_date: v })} />
        </Row>

        <Row label="End date">
          <InlineDate value={engagement.end_date} onSave={(v) => onSave({ end_date: v })} />
        </Row>

        <Row label="Currency">
          <InlineText
            value={engagement.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            className="font-mono"
            helper="3-letter ISO code (e.g. EUR, USD)."
          />
        </Row>

        <Row label="Total contract value">
          <InlineNumber
            value={engagement.total_contract_value}
            onSave={(v) => onSave({ total_contract_value: v })}
            displayFormat={(v) => v
              ? <span className="font-mono">{Number(v).toFixed(2)} {engagement.currency_code}</span>
              : <Dash />}
          />
        </Row>

        <Row label="Status">
          <InlineStatus
            value={engagement.status}
            colorMap={STATUS_COLOR}
            options={[
              { value: 'DRAFT', label: 'Draft' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ON_HOLD', label: 'On hold' },
              { value: 'DELIVERED', label: 'Delivered' },
              { value: 'CLOSED', label: 'Closed' },
              { value: 'CANCELLED', label: 'Cancelled' },
            ]}
            onSave={(v) => onSave({ status: v })}
          />
        </Row>

        <Row label="Notes">
          <InlineText
            value={engagement.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Add notes"
          />
        </Row>
      </dl>
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Projects tab ───────────────────
// Lists projects under this engagement. Rows navigate to the project's
// record page (where the full inline-edit + drawer pattern lives).
// Create drawer here pre-binds engagement = current.

type ProjectDraft = {
  name: string
  billing_type: Project['billing_type']
  currency_code: string
  status: Project['status']
  manager_person: number | null
  start_date: string | null
  end_date: string | null
}

function ProjectsTab({
  engagementId, engagementCurrency, persons,
}: {
  engagementId: number
  engagementCurrency: string
  persons: { id: number; full_name: string }[]
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const projectsQ = useQuery({
    queryKey: ['consulting', 'projects', { engagement: engagementId }],
    queryFn: async () => (await api.get<Project[]>(`/consulting/projects/?engagement=${engagementId}`)).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<ProjectDraft>(emptyDraft(engagementCurrency))

  function openCreate() {
    setDraft(emptyDraft(engagementCurrency))
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Project>('/consulting/projects/', {
      engagement: engagementId,
      name: draft.name,
      description: '',
      billing_type: draft.billing_type,
      currency_code: draft.currency_code,
      status: draft.status,
      manager_person: draft.manager_person,
      start_date: draft.start_date,
      end_date: draft.end_date,
      // Sensible defaults — finish setup on the record page.
      budget_amount: '0',
      budget_hours: '0',
      nte_amount: null,
      bill_rate_amount: null,
      bill_rate_unit: '',
      default_revenue_account: null,
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['consulting', 'projects'] })
      qc.invalidateQueries({ queryKey: ['consulting', 'engagement', engagementId] })
      toast.success('Project created')
      setCreating(false)
      navigate(`/admin/consulting/projects/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Project> }) =>
      (await api.patch(`/consulting/projects/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'projects'] }),
  })
  const saveField = async (id: number, payload: Partial<Project>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const projects = projectsQ.data || []

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Projects under this engagement. Click a row to open the project record page where you manage phases, tasks, deliverables, milestones, and assignments.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add project</Button>
      </div>

      {projectsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="text-muted-foreground text-sm">No projects under this engagement yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Billing</Th>
                <Th>Manager</Th>
                <Th className="text-right">Budget</Th>
                <Th>Period</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/consulting/projects/${p.id}`)}
                >
                  <Td className="font-mono text-xs">{p.code}</Td>
                  <Td className="font-medium">{p.name}</Td>
                  <Td className="text-xs">{BILLING_LABEL[p.billing_type]}</Td>
                  <Td className="text-xs">{p.manager_name || <Dash />}</Td>
                  <Td className="text-right text-xs font-mono">
                    {Number(p.budget_amount).toFixed(2)} {p.currency_code}
                  </Td>
                  <Td className="text-xs">
                    {p.start_date || <Dash />} → {p.end_date || <span className="text-muted-foreground">open</span>}
                  </Td>
                  <Td onClick={(ev) => ev.stopPropagation()}>
                    <InlineStatus
                      value={p.status}
                      colorMap={STATUS_COLOR}
                      options={[
                        { value: 'DRAFT', label: 'Draft' },
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'ON_HOLD', label: 'On hold' },
                        { value: 'DELIVERED', label: 'Delivered' },
                        { value: 'CLOSED', label: 'Closed' },
                        { value: 'CANCELLED', label: 'Cancelled' },
                      ]}
                      onSave={(v) => saveField(p.id, { status: v })}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New project"
        description={`Create a project under this engagement. Budget, rates, phases, and team assignments are filled in on the project's record page.`}
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="proj-name">Project name *</FieldLabel>
            <Input id="proj-name" autoFocus required value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="proj-billing">Billing type</FieldLabel>
              <Select value={draft.billing_type}
                onValueChange={(v) => setDraft({ ...draft, billing_type: v as Project['billing_type'] })}>
                <SelectTrigger id="proj-billing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(BILLING_LABEL) as Project['billing_type'][]).map((k) => (
                    <SelectItem key={k} value={k}>{BILLING_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldDescription>Drives where rates and NTE apply on the Cost tab.</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="proj-currency">Currency</FieldLabel>
              <Input id="proj-currency" value={draft.currency_code}
                onChange={(e) => setDraft({ ...draft, currency_code: e.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
              <FieldDescription>Defaults to the engagement currency ({engagementCurrency}).</FieldDescription>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="proj-manager">Manager</FieldLabel>
            <SearchableSelect
              id="proj-manager"
              value={draft.manager_person ? String(draft.manager_person) : NONE}
              onChange={(v) => setDraft({ ...draft, manager_person: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="proj-start">Start date</FieldLabel>
              <DatePicker id="proj-start" value={draft.start_date || ''}
                onChange={(v) => setDraft({ ...draft, start_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="proj-end">End date</FieldLabel>
              <DatePicker id="proj-end" value={draft.end_date || ''}
                onChange={(v) => setDraft({ ...draft, end_date: v || null })} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="proj-status">Status</FieldLabel>
            <Select value={draft.status}
              onValueChange={(v) => setDraft({ ...draft, status: v as Project['status'] })}>
              <SelectTrigger id="proj-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="ON_HOLD">On hold</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function emptyDraft(currency: string): ProjectDraft {
  return {
    name: '',
    billing_type: 'T_AND_M',
    currency_code: currency || 'EUR',
    status: 'DRAFT',
    manager_person: null,
    start_date: null,
    end_date: null,
  }
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
